import React, { useState, useEffect } from 'react';
import { Target, Check, X, Clock } from 'lucide-react';

const SharedGoalsBoard = () => {
  const [goalsData, setGoalsData] = useState(null);
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadGoalsBoard();
  }, []);

  const loadGoalsBoard = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(
        `${BACKEND_URL}/api/member/accountability/v2/goals/board`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setGoalsData(data);
      }
    } catch (error) {
      console.error('Error loading goals board:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <Check className="w-5 h-5 text-green-600" />;
      case 'partially_completed':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'not_completed':
        return <X className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'border-green-200 bg-green-50';
      case 'partially_completed':
        return 'border-yellow-200 bg-yellow-50';
      case 'not_completed':
        return 'border-red-200 bg-red-50';
      default:
        return 'border-gray-200 bg-white';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  if (!goalsData) {
    return (
      <div className="text-center p-12">
        <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">No goals set yet. Complete your weekly check-in to set goals!</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Week Header */}
      <div className="text-center">
        <h3 className="text-xl font-bold text-amber-900">
          Week {goalsData.week_number} - {goalsData.year}
        </h3>
        <p className="text-gray-600">Shared Weekly Goals</p>
      </div>

      {/* Your Goals */}
      <div className="bg-gradient-to-br from-amber-50 to-amber-100 border-2 border-amber-200 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-amber-600 rounded-full flex items-center justify-center">
            <Target className="w-6 h-6 text-white" />
          </div>
          <div>
            <h4 className="font-bold text-amber-900">{goalsData.your_goals.name}'s Goals</h4>
            <p className="text-sm text-amber-700">Your commitments for this week</p>
          </div>
        </div>

        {goalsData.your_goals.goals.length === 0 ? (
          <div className="text-center py-8 text-amber-700">
            <p>No goals set yet. Complete your weekly check-in!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {goalsData.your_goals.goals.map((goal, idx) => (
              <div
                key={idx}
                className={`p-4 border-2 rounded-lg ${getStatusColor(
                  goalsData.your_goals.completion_status[idx]
                )}`}
              >
                <div className="flex items-start gap-3">
                  {getStatusIcon(goalsData.your_goals.completion_status[idx])}
                  <div className="flex-1">
                    <p className="font-medium">{goal}</p>
                  </div>
                  <span className="text-xs font-semibold text-gray-500">
                    {idx + 1}/3
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Partner's Goals */}
      <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
            <Target className="w-6 h-6 text-white" />
          </div>
          <div>
            <h4 className="font-bold text-blue-900">{goalsData.partner_goals.name}'s Goals</h4>
            <p className="text-sm text-blue-700">Your partner's commitments</p>
          </div>
        </div>

        {goalsData.partner_goals.goals.length === 0 ? (
          <div className="text-center py-8 text-blue-700">
            <p>Your partner hasn't set goals yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {goalsData.partner_goals.goals.map((goal, idx) => (
              <div
                key={idx}
                className={`p-4 border-2 rounded-lg ${getStatusColor(
                  goalsData.partner_goals.completion_status[idx]
                )}`}
              >
                <div className="flex items-start gap-3">
                  {getStatusIcon(goalsData.partner_goals.completion_status[idx])}
                  <div className="flex-1">
                    <p className="font-medium">{goal}</p>
                  </div>
                  <span className="text-xs font-semibold text-gray-500">
                    {idx + 1}/3
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <Check className="w-4 h-4 text-green-600" />
          <span>Completed</span>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-yellow-600" />
          <span>In Progress</span>
        </div>
        <div className="flex items-center gap-2">
          <X className="w-4 h-4 text-red-600" />
          <span>Not Done</span>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-gray-400" />
          <span>Pending</span>
        </div>
      </div>
    </div>
  );
};

export default SharedGoalsBoard;
